import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Building2, Briefcase, Users, Bot, HelpCircle } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Contact, Client, Partner } from '../../types/contact';

interface ContactEditDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (formData: Contact) => Promise<void>;
  contact: Contact | null;
  errors: Record<string, string>;
  clients: Client[];
  partners: Partner[];
}

export const ContactEditDialog: React.FC<ContactEditDialogProps> = ({
  open,
  onClose,
  onSubmit,
  contact,
  errors,
  clients,
  partners
}) => {
  const { t } = useTranslation();
  // Initialiser avec des valeurs par défaut typées correctement
  const [formData, setFormData] = useState<Contact>({
    name: '',
    email: '',
    role: '',
    phone: '',
    notes: '',
    contactType: 'CLIENT', // Type par défaut
    clientId: clients.length > 0 ? clients[0].id : undefined,
    partnerId: undefined,
    userId: undefined
  });
  
  // Réinitialiser le formulaire quand le contact change
  useEffect(() => {
    if (contact) {
      // Assurez-vous que tous les champs sont définis
      setFormData({
        id: contact.id,
        name: contact.name,
        email: contact.email,
        role: contact.role || '',
        phone: contact.phone || '',
        notes: contact.notes || '',
        contactType: contact.contactType || 'CLIENT',
        clientId: contact.clientId || undefined,
        partnerId: contact.partnerId || undefined,
        userId: contact.userId || undefined,
        // Copier les autres propriétés potentielles mais assurez-vous qu'elles sont typées
        client: contact.client,
        partner: contact.partner
      });
    } else {
      // Réinitialiser avec des valeurs par défaut
      setFormData({
        name: '',
        email: '',
        role: '',
        phone: '',
        notes: '',
        contactType: 'CLIENT',
        clientId: clients.length > 0 ? clients[0].id : undefined,
        partnerId: undefined,
        userId: undefined
      });
    }
  }, [contact, clients, partners]);
  
  const handleInputChange = (field: keyof Contact, value: string | number | null | undefined) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Fonction pour gérer le changement de type de contact
  const handleTypeChange = (type: string) => {
    // Faire un cast explicite pour le type
    const contactType = type as 'CLIENT' | 'PARTNER' | 'INTERNAL' | 'SYSTEM' | 'UNKNOWN';
    
    // Mise à jour du type de contact
    const newFormData: Contact = {
      ...formData,
      contactType
    };

    // Réinitialiser les IDs correspondants selon le type
    if (type === 'CLIENT') {
      newFormData.clientId = clients.length > 0 ? clients[0].id : undefined;
      newFormData.partnerId = undefined;
      newFormData.userId = undefined;
    } else if (type === 'PARTNER') {
      newFormData.partnerId = partners.length > 0 ? partners[0].id : undefined;
      newFormData.clientId = undefined;
      newFormData.userId = undefined;
    } else if (type === 'INTERNAL') {
      newFormData.userId = undefined; // À renseigner manuellement ou automatiquement
      newFormData.clientId = undefined;
      newFormData.partnerId = undefined;
    } else {
      // SYSTEM ou UNKNOWN
      newFormData.clientId = undefined;
      newFormData.partnerId = undefined;
      newFormData.userId = undefined;
    }

    setFormData(newFormData);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {contact ? t('contact.edit') : t('contact.add')}
          </DialogTitle>
          <DialogDescription>
            {t('contact.editDescription')}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit}>
          <div className="grid gap-4 py-4">
            {/* Type de contact */}
            <div className="space-y-2">
              <Label htmlFor="contactType">
                {t('contact.type')}<span className="text-red-500">*</span>
              </Label>
              <Select
                value={formData.contactType}
                onValueChange={handleTypeChange}
              >
                <SelectTrigger id="contactType">
                  <SelectValue placeholder={t('contact.selectType')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="CLIENT">
                    <div className="flex items-center">
                      <Building2 className="h-4 w-4 mr-2" />
                      {t('contact.type.client')}
                    </div>
                    </SelectItem>
                  <SelectItem value="PARTNER">
                    <div className="flex items-center">
                      <Briefcase className="h-4 w-4 mr-2" />
                      {t('contact.type.partner')}
                    </div>
                  </SelectItem>
                  <SelectItem value="INTERNAL">
                    <div className="flex items-center">
                      <Users className="h-4 w-4 mr-2" />
                      {t('contact.type.internal')}
                    </div>
                  </SelectItem>
                  <SelectItem value="SYSTEM">
                    <div className="flex items-center">
                      <Bot className="h-4 w-4 mr-2" />
                      {t('contact.type.system')}
                    </div>
                  </SelectItem>
                  <SelectItem value="UNKNOWN">
                    <div className="flex items-center">
                      <HelpCircle className="h-4 w-4 mr-2" />
                      {t('contact.type.unknown')}
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
              {errors.contactType && (
                <p className="text-destructive text-sm">{errors.contactType}</p>
              )}
            </div>

            {/* Informations de base du contact */}
            <div className="grid grid-cols-1 gap-4">
              {/* Nom */}
              <div className="space-y-2">
                <Label htmlFor="name">
                  {t('contact.name')}<span className="text-red-500">*</span>
                </Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                />
                {errors.name && (
                  <p className="text-destructive text-sm">{errors.name}</p>
                )}
              </div>
              
              {/* Email */}
              <div className="space-y-2">
                <Label htmlFor="email">
                  {t('contact.email')}<span className="text-red-500">*</span>
                </Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                />
                {errors.email && (
                  <p className="text-destructive text-sm">{errors.email}</p>
                )}
              </div>
              
              {/* Rôle */}
              <div className="space-y-2">
                <Label htmlFor="role">
                  {t('contact.role')}
                </Label>
                <Input
                  id="role"
                  value={formData.role || ''}
                  onChange={(e) => handleInputChange('role', e.target.value)}
                />
                {errors.role && (
                  <p className="text-destructive text-sm">{errors.role}</p>
                )}
              </div>
              
              {/* Client (affiché seulement si le type est CLIENT) */}
              {formData.contactType === 'CLIENT' && (
                <div className="space-y-2">
                  <Label htmlFor="clientId">
                    {t('contact.client')}<span className="text-red-500">*</span>
                  </Label>
                  <Select
                    value={formData.clientId?.toString() || ''}
                    onValueChange={(value) => handleInputChange('clientId', value ? parseInt(value) : undefined)}
                  >
                    <SelectTrigger id="clientId">
                      <SelectValue placeholder={t('contact.selectClient')} />
                    </SelectTrigger>
                    <SelectContent>
                      {clients.map((client) => (
                        <SelectItem key={client.id} value={client.id.toString()}>
                          {client.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {errors.clientId && (
                    <p className="text-destructive text-sm">{errors.clientId}</p>
                  )}
                </div>
              )}
              
              {/* Partenaire (affiché seulement si le type est PARTNER) */}
              {formData.contactType === 'PARTNER' && (
                <div className="space-y-2">
                  <Label htmlFor="partnerId">
                    {t('contact.partner')}<span className="text-red-500">*</span>
                  </Label>
                  <Select
                    value={formData.partnerId?.toString() || ''}
                    onValueChange={(value) => handleInputChange('partnerId', value ? parseInt(value) : undefined)}
                  >
                    <SelectTrigger id="partnerId">
                      <SelectValue placeholder={t('contact.selectPartner')} />
                    </SelectTrigger>
                    <SelectContent>
                      {partners.map((partner) => (
                        <SelectItem key={partner.id} value={partner.id.toString()}>
                          {partner.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {errors.partnerId && (
                    <p className="text-destructive text-sm">{errors.partnerId}</p>
                  )}
                </div>
              )}
              
              {/* Téléphone */}
              <div className="space-y-2">
                <Label htmlFor="phone">{t('contact.phone')}</Label>
                <Input
                  id="phone"
                  value={formData.phone || ''}
                  onChange={(e) => handleInputChange('phone', e.target.value || null)}
                />
              </div>
              
              {/* Notes */}
              <div className="space-y-2">
                <Label htmlFor="notes">{t('contact.notes')}</Label>
                <Textarea
                  id="notes"
                  value={formData.notes || ''}
                  onChange={(e) => handleInputChange('notes', e.target.value || null)}
                  rows={4}
                />
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" type="button" onClick={onClose}>
              {t('common.cancel')}
            </Button>
            <Button type="submit">
              {contact ? t('common.save') : t('common.create')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
